import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AchievementUnlock } from '@ember/shared';

export interface FxPayload {
  xpEarned: number;
  levelUp?: { from: number; to: number };
  achievementsUnlocked: AchievementUnlock[];
}

type QueueItem =
  | { kind: 'xp'; amount: number }
  | { kind: 'levelUp'; from: number; to: number }
  | { kind: 'achievement'; achievement: AchievementUnlock };

interface State {
  queue: QueueItem[];
  showingLevelUp: { from: number; to: number } | null;
  showingAchievement: AchievementUnlock | null;
  xpBurst: number;
}

type Action =
  | { type: 'ENQUEUE'; items: QueueItem[] }
  | { type: 'NEXT' }
  | { type: 'DISMISS_LEVEL_UP' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'ENQUEUE':
      return { ...state, queue: [...state.queue, ...action.items] };
    case 'DISMISS_LEVEL_UP':
      return { ...state, showingLevelUp: null };
    case 'NEXT': {
      const [head, ...rest] = state.queue;
      if (!head) {
        return { ...state, showingAchievement: null, xpBurst: 0 };
      }
      if (head.kind === 'xp') {
        return { ...state, queue: rest, xpBurst: head.amount };
      }
      if (head.kind === 'levelUp') {
        return { ...state, queue: rest, showingLevelUp: { from: head.from, to: head.to } };
      }
      return { ...state, queue: rest, showingAchievement: head.achievement };
    }
  }
}

interface FxContextValue {
  enqueue: (payload: FxPayload) => void;
  dismissLevelUp: () => void;
  showingLevelUp: { from: number; to: number } | null;
  showingAchievement: AchievementUnlock | null;
  xpBurst: number;
}

const FxContext = createContext<FxContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

const ACHIEVEMENT_DELAY_MS = 1500;
const XP_BURST_MS = 800;

export function GamificationFxProvider({ children }: ProviderProps) {
  const [state, dispatch] = useReducer(reducer, {
    queue: [],
    showingLevelUp: null,
    showingAchievement: null,
    xpBurst: 0,
  });
  const [isDraining, setIsDraining] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enqueue = useCallback((payload: FxPayload) => {
    const items: QueueItem[] = [];
    if (payload.xpEarned > 0) items.push({ kind: 'xp', amount: payload.xpEarned });
    if (payload.levelUp) {
      items.push({ kind: 'levelUp', from: payload.levelUp.from, to: payload.levelUp.to });
    }
    for (const a of payload.achievementsUnlocked) {
      items.push({ kind: 'achievement', achievement: a });
    }
    if (items.length > 0) dispatch({ type: 'ENQUEUE', items });
  }, []);

  const dismissLevelUp = useCallback(() => dispatch({ type: 'DISMISS_LEVEL_UP' }), []);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (state.showingLevelUp) {
      return;
    }

    if (state.queue.length === 0 && !state.showingAchievement && state.xpBurst === 0) {
      if (isDraining) setIsDraining(false);
      return;
    }

    if (!isDraining) setIsDraining(true);

    if (state.xpBurst > 0) {
      timerRef.current = setTimeout(() => dispatch({ type: 'NEXT' }), XP_BURST_MS);
      return;
    }
    if (state.showingAchievement) {
      timerRef.current = setTimeout(() => dispatch({ type: 'NEXT' }), ACHIEVEMENT_DELAY_MS);
      return;
    }
    dispatch({ type: 'NEXT' });
  }, [state, isDraining]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const value = useMemo<FxContextValue>(
    () => ({
      enqueue,
      dismissLevelUp,
      showingLevelUp: state.showingLevelUp,
      showingAchievement: state.showingAchievement,
      xpBurst: state.xpBurst,
    }),
    [enqueue, dismissLevelUp, state.showingLevelUp, state.showingAchievement, state.xpBurst],
  );

  return <FxContext.Provider value={value}>{children}</FxContext.Provider>;
}

export function useGamificationFx(): FxContextValue {
  const ctx = useContext(FxContext);
  if (!ctx) throw new Error('useGamificationFx must be used within GamificationFxProvider');
  return ctx;
}
